import { ActionIcon, Button, Loader, Modal, Radio, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ListVideo, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import { EditProjectCreate } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { CardPicture } from "../../ui/CardPicture";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { PageColumns } from "../../ui/PageColumns";
import { TitledHeader } from "../../ui/TitledHeader";
import { formatDate } from "../../ui/format";
import {
  ASPECT_RATIOS,
  PROJECT_NAME_MAX,
  cssRatio,
  useCreateProject,
  useDeleteProject,
  useProjectPages,
  type AspectRatio,
  type ProjectSummary,
} from "./project";
import { stripFrame } from "./clip-strip";
import classes from "./editor.module.css";
import { formatLength } from "./time";

const NAME_PROBLEM = `Give the project a name of up to ${PROJECT_NAME_MAX} characters.`;
const RATIO_LABEL: Record<AspectRatio, string> = {
  "16:9": "16:9 Landscape",
  "9:16": "9:16 Portrait",
  "1:1": "1:1 Square",
  "4:5": "4:5 Feed",
  "4:3": "4:3 Classic",
  "21:9": "21:9 Ultrawide",
};

export function ProjectListScreen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/editor",
  });
  return <ProjectList teamId={teamId} brandId={brandId} />;
}

/** The brand's edit projects, newest change first, 24 a page as the list scrolls (spec
 * editor/project-list-pages.md), and "New project" (E1), wherever its frame is (Classic's, or
 * Pro's: spec ui-perf/ui-switch-classic-simple-pro, PR e6). */
export function ProjectList({ teamId, brandId }: { teamId: string; brandId: string }) {
  const projects = useProjectPages(teamId, brandId);
  const items = projects.data ?? [];
  const [creating, setCreating] = useState(false);
  const newButton = (
    <Button
      variant="outline"
      className={classes.newProject}
      leftSection={<Plus size={20} />}
      onClick={() => setCreating(true)}
    >
      New project
    </Button>
  );
  return (
    <PageColumns
      top={
        <TitledHeader
          title="Video Editor"
          subtitle="Cut the brand's videos into one, with titles, and export an MP4."
          right={newButton}
        />
      }
    >
      {projects.isPending ? <Loader size="sm" aria-label="Loading projects" /> : null}
      {projects.isError ? <ErrorAlert error={projects.error} /> : null}
      {projects.isSuccess && items.length === 0 ? (
        <EmptyState
          image={<ListVideo size={48} strokeWidth={1.5} aria-hidden />}
          title="No projects yet"
          description="Start one to cut the brand's videos and generations into an ad."
          action={newButton}
        />
      ) : null}
      {items.length > 0 ? (
        <ul className={classes.grid} aria-label="Projects">
          {items.map((project) => (
            <li key={project.id}>
              <ProjectCard teamId={teamId} brandId={brandId} project={project} />
            </li>
          ))}
        </ul>
      ) : null}
      <LoadAhead more={morePages(projects)} loaded={items.length} label="Loading more projects" />
      <NewProjectModal
        teamId={teamId}
        brandId={brandId}
        opened={creating}
        onClose={() => setCreating(false)}
      />
    </PageColumns>
  );
}

function ProjectCard(props: { teamId: string; brandId: string; project: ProjectSummary }) {
  const { teamId, brandId, project } = props;
  const clips = project.clipCount;
  const facts = [
    project.aspectRatio,
    `${clips} clip${clips === 1 ? "" : "s"}`,
    formatLength(project.durationMs),
    `edited ${formatDate(project.updatedAt)}`,
  ];
  // With no picture clip, a neutral area with its ratio's outline.
  const placeholder = <span className={classes.ratioBox} style={{ aspectRatio: cssRatio(project.aspectRatio) }} />;
  // Its creator, an admin or the owner deletes it (D6); a hint only, the API refuses the rest.
  const me = useMembership(teamId);
  const deletable = me !== undefined && canEditContent(me.role, project.createdBy, me.userId);
  const [asking, setAsking] = useState(false);
  const remove = useDeleteProject(teamId, brandId);
  return (
    <div className={classes.projectItem}>
      <Link
        to="/teams/$teamId/brands/$brandId/editor/$projectId"
        params={{ teamId, brandId, projectId: project.id }}
        className={classes.projectCard}
      >
        <span className={classes.projectFrame} aria-hidden>
          {/* Its first picture's poster (decision 21: ours, as live lists no editor projects). */}
          {project.poster !== null ? (
            <CardPicture
              src={project.poster}
              alt=""
              fallback={<span className={classes.projectFallback}>{placeholder}</span>}
            />
          ) : project.video !== null ? (
            <FirstFrame video={project.video} placeholder={placeholder} />
          ) : (
            placeholder
          )}
        </span>
        <Text fw={600} truncate>
          {project.name}
        </Text>
        <Text size="xs" c="var(--cai-color-text-muted)">
          {facts.join(" · ")}
        </Text>
      </Link>
      {deletable ? (
        // The library's bin on a tile, on hover or focus (decision 21: live's app has no delete).
        <Tooltip label="Delete">
          <ActionIcon
            variant="default"
            className={classes.projectBin}
            aria-label={`Delete ${project.name}`}
            onClick={() => setAsking(true)}
          >
            <Trash2 size={16} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <ConfirmModal
        opened={asking}
        title="Delete project?"
        confirmLabel="Delete"
        pending={remove.isPending}
        error={remove.isError ? <ErrorAlert error={remove.error} /> : undefined}
        onCancel={() => {
          remove.reset();
          setAsking(false);
        }}
        onConfirm={() =>
          remove.mutate(
            { teamId, brandId, projectId: project.id },
            { onSuccess: () => setAsking(false) },
          )
        }
      >
        <Text size="sm">
          {project.name} and its edits are deleted. Videos it saved stay in the Library.
        </Text>
      </ConfirmModal>
    </div>
  );
}

/**
 * A video's first frame, drawn in the browser as the timeline's strip draws it (`clip-strip.ts`), on
 * a card whose video has no poster yet (E1 decision 21); the placeholder until then, or where it
 * cannot be drawn.
 */
function FirstFrame({ video, placeholder }: { video: string; placeholder: ReactNode }) {
  const [frame, setFrame] = useState<{ video: string; image: string | null } | null>(null);
  useEffect(() => {
    let current = true;
    void stripFrame(video).then((image) => {
      if (current) setFrame({ video, image });
    });
    return () => {
      current = false;
    };
  }, [video]);
  const image = frame?.video === video ? frame.image : null;
  return image === null ? placeholder : <CardPicture src={image} alt="" />;
}

function NewProjectModal(props: {
  teamId: string;
  brandId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { teamId, brandId, opened, onClose } = props;
  const navigate = useNavigate();
  const create = useCreateProject(teamId, brandId);
  const [name, setName] = useState("");
  const [ratio, setRatio] = useState<AspectRatio>("16:9");
  const [invalid, setInvalid] = useState(false);

  const close = () => {
    create.reset();
    setInvalid(false);
    onClose();
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = EditProjectCreate.safeParse({ name: name.trim(), aspect: ratio });
    setInvalid(!parsed.success);
    if (!parsed.success) return;
    create
      .create({ name: parsed.data.name, aspectRatio: parsed.data.aspect })
      .then((created) => {
        setName("");
        close();
        void navigate({
          to: "/teams/$teamId/brands/$brandId/editor/$projectId",
          params: { teamId, brandId, projectId: created.id },
        });
      })
      // The failure shows from the mutation's state, under the form.
      .catch(() => undefined);
  };

  return (
    <Modal opened={opened} onClose={close} title="New project" centered>
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <TextInput
            label="Name"
            data-autofocus
            maxLength={PROJECT_NAME_MAX}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            error={invalid ? NAME_PROBLEM : undefined}
          />
          <Radio.Group
            label="Aspect ratio"
            value={ratio}
            onChange={(value) => {
              const next = ASPECT_RATIOS.find((each) => each === value);
              if (next !== undefined) setRatio(next);
            }}
          >
            <Stack gap={8} mt={6}>
              {ASPECT_RATIOS.map((each) => (
                <Radio key={each} value={each} label={RATIO_LABEL[each]} />
              ))}
            </Stack>
          </Radio.Group>
          {create.isError ? <ErrorAlert error={create.error} /> : null}
          <Button type="submit" loading={create.isPending}>
            Create project
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
